import { useTheme } from '@mui/material';
import { driver, type Driver, type PopoverDOM } from 'driver.js';
import { useReducedMotion } from 'framer-motion';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';
import 'driver.js/dist/driver.css';

import { GuidedJourneyContextProvider } from '@/components/guidedJourney/GuidedJourney.context.ts';
import { GuidedJourneyStep } from '@/components/guidedJourney/model/GuidedJourney.model.ts';
import {
  canAutoShowJourney,
  completeJourneyProgress,
  getJourneyStorageKey,
  readJourneyProgress,
  registerJourneyAppearance,
  saveJourneyProgress,
  suppressJourneyProgress,
} from '@/components/guidedJourney/util/guidedJourneyProgress.util.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useImpersonationStatus } from '@/views/adminPanel/components/impersonation/util/useImpersonation.util.ts';

import './guidedJourney.css';

const MAXIMUM_APPEARANCES = 3;
const TARGET_TIMEOUT = 4000;
const PASSTHROUGH_CLASS = 'jain-journey-passthrough';

const getStepKey = (step: GuidedJourneyStep): string =>
  `${step.journeyId}:v${step.version}:${step.id}`;

const waitForTarget = (target: string, timeout: number): Promise<Element | null> =>
  new Promise(resolve => {
    const existingElement = document.querySelector(target);
    if (existingElement) {
      resolve(existingElement);
      return;
    }

    let settled = false;
    const finish = (element: Element | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
      resolve(element);
    };
    const observer = new MutationObserver(() => {
      const element = document.querySelector(target);
      if (element) finish(element);
    });
    const timeoutId = window.setTimeout(() => finish(document.querySelector(target)), timeout);

    observer.observe(document.body, { childList: true, subtree: true });
  });

export const GuidedJourneyProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const driverRef = useRef<Driver | null>(null);
  const activeStepKeyRef = useRef<string | null>(null);
  const closedJourneysRef = useRef<Set<string>>(new Set());
  const appearedJourneysRef = useRef<Set<string>>(new Set());
  const passedStepsRef = useRef<Set<string>>(new Set());
  const requestSequenceRef = useRef(0);
  const prefersReducedMotion = useReducedMotion();
  const theme = useTheme();
  const { email, userId } = useUserContext();
  const { isLoadingStatus, status: impersonationStatus } = useImpersonationStatus();
  const { t } = useTranslationWithPrefix('components.guidedJourney');

  const storageNamespace = useMemo(() => {
    if (!userId || isLoadingStatus) return null;
    if (!impersonationStatus?.impersonating) return `user:${userId}`;

    const administrator = impersonationStatus.realAdmin?.email ?? email;
    return `impersonation:${administrator}:${userId}`;
  }, [email, impersonationStatus, isLoadingStatus, userId]);

  const destroyDriver = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
    activeStepKeyRef.current = null;
    document.documentElement.classList.remove(PASSTHROUGH_CLASS);
  }, []);

  const getStorageKey = useCallback(
    (journeyId: string, version: number) =>
      storageNamespace ? getJourneyStorageKey(storageNamespace, journeyId, version) : null,
    [storageNamespace],
  );

  const suppressJourney = useCallback(
    (journeyId: string, version: number) => {
      const storageKey = getStorageKey(journeyId, version);
      if (!storageKey) return;

      const progress = readJourneyProgress(window.localStorage, storageKey, version);
      saveJourneyProgress(window.localStorage, storageKey, suppressJourneyProgress(progress));
      closedJourneysRef.current.add(storageKey);
      destroyDriver();
    },
    [destroyDriver, getStorageKey],
  );

  const temporarilyCloseJourney = useCallback(
    (journeyId: string, version: number) => {
      const storageKey = getStorageKey(journeyId, version);
      if (storageKey) closedJourneysRef.current.add(storageKey);
      destroyDriver();
    },
    [destroyDriver, getStorageKey],
  );

  const addPopoverActions = useCallback(
    (popover: PopoverDOM, step: GuidedJourneyStep) => {
      popover.closeButton.setAttribute('aria-label', t('close'));

      if (step.secondaryAction) {
        const secondaryButton = document.createElement('button');
        secondaryButton.className = 'jain-guided-journey__secondary-action';
        secondaryButton.textContent = step.secondaryAction.label;
        secondaryButton.type = 'button';
        secondaryButton.addEventListener(
          'click',
          () => {
            destroyDriver();
            step.secondaryAction?.onClick();
          },
          { once: true },
        );
        popover.footerButtons.prepend(secondaryButton);
      }

      const suppressButton = document.createElement('button');
      suppressButton.className = 'jain-guided-journey__suppress';
      suppressButton.textContent = t('dontShowAgain');
      suppressButton.type = 'button';
      suppressButton.addEventListener(
        'click',
        () => suppressJourney(step.journeyId, step.version),
        { once: true },
      );
      popover.footer.append(suppressButton);
    },
    [destroyDriver, suppressJourney, t],
  );

  const requestStep = useCallback(
    (step: GuidedJourneyStep) => {
      const requestSequence = ++requestSequenceRef.current;
      const stepKey = getStepKey(step);
      const storageKey = getStorageKey(step.journeyId, step.version);
      let cancelled = false;

      if (
        !storageKey ||
        closedJourneysRef.current.has(storageKey) ||
        passedStepsRef.current.has(stepKey) ||
        activeStepKeyRef.current === stepKey
      ) {
        return () => undefined;
      }

      const showStep = async () => {
        const progress = readJourneyProgress(window.localStorage, storageKey, step.version);
        const hasAppeared = appearedJourneysRef.current.has(storageKey);
        if (!hasAppeared && !canAutoShowJourney(progress, MAXIMUM_APPEARANCES)) return;

        const target = await waitForTarget(step.target, TARGET_TIMEOUT);
        if (!target || cancelled || requestSequence !== requestSequenceRef.current) return;

        target.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
        await new Promise<void>(resolve => {
          window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
        });
        if (cancelled || requestSequence !== requestSequenceRef.current) return;

        if (!hasAppeared) {
          saveJourneyProgress(window.localStorage, storageKey, registerJourneyAppearance(progress));
          appearedJourneysRef.current.add(storageKey);
        }

        destroyDriver();
        activeStepKeyRef.current = stepKey;
        document.body.style.setProperty('--guided-journey-accent', theme.colors.accent);
        document.body.style.setProperty('--guided-journey-bg', theme.colors.bgCard);
        document.body.style.setProperty('--guided-journey-border', theme.colors.border);
        document.body.style.setProperty('--guided-journey-muted', theme.colors.textSecondary);
        document.body.style.setProperty('--guided-journey-text', theme.colors.textPrimary);

        const journeyDriver = driver({
          allowClose: true,
          allowKeyboardControl: true,
          allowScroll: true,
          animate: !prefersReducedMotion,
          duration: prefersReducedMotion ? 0 : 220,
          onCloseClick: () => temporarilyCloseJourney(step.journeyId, step.version),
          onDestroyed: () => {
            if (driverRef.current === journeyDriver) driverRef.current = null;
            if (activeStepKeyRef.current === stepKey) activeStepKeyRef.current = null;
            document.documentElement.classList.remove(PASSTHROUGH_CLASS);
          },
          onPopoverRender: popover => addPopoverActions(popover, step),
          overlayColor: theme.palette.mode === 'dark' ? '#02040b' : '#111827',
          overlayOpacity: theme.palette.mode === 'dark' ? 0.72 : 0.58,
          popoverClass: 'jain-guided-journey',
          showButtons: step.primaryAction ? ['next', 'close'] : ['close'],
          showProgress: !!step.progressText,
          smoothScroll: !prefersReducedMotion,
          stagePadding: 8,
          stageRadius: 16,
        });

        driverRef.current = journeyDriver;
        if (step.nonBlocking) document.documentElement.classList.add(PASSTHROUGH_CLASS);
        journeyDriver.highlight({
          disableActiveInteraction: false,
          element: target,
          popover: {
            align: step.align ?? 'center',
            description: step.description,
            nextBtnText: step.primaryAction?.label,
            onNextClick: step.primaryAction
              ? () => {
                  destroyDriver();
                  step.primaryAction?.onClick();
                }
              : undefined,
            progressText: step.progressText,
            showButtons: step.primaryAction ? ['next', 'close'] : ['close'],
            showProgress: !!step.progressText,
            side: step.side ?? 'bottom',
            title: step.title,
          },
        });
      };

      window.setTimeout(() => void showStep(), prefersReducedMotion ? 0 : 450);

      return () => {
        cancelled = true;
        if (activeStepKeyRef.current === stepKey) destroyDriver();
      };
    },
    [
      addPopoverActions,
      destroyDriver,
      getStorageKey,
      prefersReducedMotion,
      temporarilyCloseJourney,
      theme,
    ],
  );

  const advanceStep = useCallback(
    (journeyId: string, version: number, stepId: string) => {
      passedStepsRef.current.add(`${journeyId}:v${version}:${stepId}`);
      destroyDriver();
    },
    [destroyDriver],
  );

  const completeJourney = useCallback(
    (journeyId: string, version: number) => {
      const storageKey = getStorageKey(journeyId, version);
      if (!storageKey) return;

      const progress = readJourneyProgress(window.localStorage, storageKey, version);
      saveJourneyProgress(window.localStorage, storageKey, completeJourneyProgress(progress));
      closedJourneysRef.current.add(storageKey);
      destroyDriver();
    },
    [destroyDriver, getStorageKey],
  );

  useEffect(() => destroyDriver, [destroyDriver]);

  const contextValue = useMemo(
    () => ({ advanceStep, completeJourney, requestStep }),
    [advanceStep, completeJourney, requestStep],
  );

  return (
    <GuidedJourneyContextProvider value={contextValue}>{children}</GuidedJourneyContextProvider>
  );
};
